import { Divider, Flex, Group, Loader, Text } from '@mantine/core';
import {
  ChatState,
  EmailDraft,
  ErrorMarker,
  isClientDataProposal,
  Message,
  MessageDirection,
  ProposedAction,
} from '../chat/types';
import { VerifyOutcome } from '../chat/verify/types';
import { ChatErrorBubble } from './ChatErrorBubble';
import { ChatFileCardRow } from './ChatFileCardRow';
import { EmailDraftCard, OutlookEmailContext } from './EmailDraftCard';
import { PresentedFileCard } from './PresentedFileCard';
import { ScenarioUpdateCard } from './ScenarioUpdateCard';
import { SiestaMessage } from './SiestaMessage';
import { ClientDataProposalWidget } from './ClientDataProposalWidget';
import { TemplateProposalWidget } from './TemplateProposalWidget';
import { ToolLimitReachedWidget } from './ToolLimitReachedWidget';
import { UserMessage } from './UserMessage';

type Props = {
  messages: Message[];
  latestIncomingId?: string;
  isStreaming?: boolean;
  isReconnecting?: boolean;
  onContinue: (widgetMessageId: string) => void;
  onProposalDecision?: (
    widgetMessageId: string | null,
    proposal: ProposedAction,
    approve: boolean,
  ) => void;
  // Pending proposals recovered from the server after a reload, when no in-memory message
  // carries the widget any more.
  orphanProposals?: ProposedAction[];
  // Email drafts recovered the same way on reload.
  orphanEmailDrafts?: EmailDraft[];
  // Message index the recovered drafts render at: the end of the loaded history block, so
  // live messages sent afterwards appear below them. Defaults to after every message.
  orphanAnchorIndex?: number;
  outlook?: OutlookEmailContext;
  isDecidingProposal?: boolean;
  onVerifyAction?: (outcome: VerifyOutcome, note?: string) => void;
  onRetry?: (marker: ErrorMarker) => void;
  dark?: boolean;
};

export function SiestaBarMessages({
  messages,
  latestIncomingId,
  isStreaming,
  isReconnecting,
  onContinue,
  onProposalDecision,
  orphanProposals,
  orphanEmailDrafts,
  orphanAnchorIndex,
  outlook,
  isDecidingProposal,
  onVerifyAction,
  onRetry,
  dark,
}: Props) {
  const renderProposal = (widgetMessageId: string | null, proposal: ProposedAction) =>
    isClientDataProposal(proposal) ? (
      <ClientDataProposalWidget
        key={proposal.proposalId}
        proposal={proposal}
        onDecide={approve => onProposalDecision?.(widgetMessageId, proposal, approve)}
        disabled={isStreaming || isDecidingProposal}
      />
    ) : (
      <TemplateProposalWidget
        key={proposal.proposalId}
        proposal={proposal}
        onDecide={approve => onProposalDecision?.(widgetMessageId, proposal, approve)}
        disabled={isStreaming || isDecidingProposal}
      />
    );

  const renderProposals = (message: Extract<Message, { kind: 'message' }>) =>
    message.proposedActions?.map(proposal => renderProposal(message.id, proposal));

  // A draft is expired once the conversation moves on past it: a newer user message exists
  // after the message that carried it. Expired cards render collapsed (expandable) so old
  // drafts don't pile up as full-size actionable cards.
  const lastOutgoingIndex = messages.reduce(
    (last, message, index) =>
      message.kind === 'message' && message.direction === MessageDirection.Outgoing ? index : last,
    -1,
  );
  const lastChatMessage = messages.filter(message => message.kind === 'message').at(-1);
  const lastMessageIsIncoming = lastChatMessage?.direction === MessageDirection.Incoming;
  // Recovered drafts sit at the end of the loaded history. Only the newest one is still
  // "current" — and only while nothing has been sent after the history block and the
  // conversation ends on an assistant reply.
  const anchorIndex = Math.min(orphanAnchorIndex ?? messages.length, messages.length);
  const nothingAfterHistory = anchorIndex >= messages.length;
  const newestOrphanDraftId = orphanEmailDrafts?.at(-1)?.draftId;

  const renderOrphanDrafts = () =>
    orphanEmailDrafts?.map(draft => (
      <EmailDraftCard
        key={draft.draftId}
        draft={draft}
        outlook={outlook}
        expired={
          !(nothingAfterHistory && lastMessageIsIncoming && draft.draftId === newestOrphanDraftId)
        }
      />
    ));

  const renderEmailDrafts = (
    message: Extract<Message, { kind: 'message' }>,
    messageIndex: number,
  ) =>
    message.emailDrafts?.map(draft => (
      <EmailDraftCard
        key={draft.draftId}
        draft={draft}
        outlook={outlook}
        expired={messageIndex < lastOutgoingIndex}
      />
    ));

  const renderMessage = (message: Message, messageIndex: number) => {
    if (message.kind === 'interrupted') {
      return (
        <Divider key={message.id} label="Interrupted" labelPosition="center" c="dimmed" my="xs" />
      );
    }
    if (message.kind === 'failed') {
      return (
        <Divider
          key={message.id}
          label="This response didn't finish"
          labelPosition="center"
          c="dimmed"
          my="xs"
        />
      );
    }
    if (message.kind === 'error') {
      return (
        <ChatErrorBubble
          key={message.id}
          onRetry={message.retryPayload ? () => onRetry?.(message) : undefined}
          disabled={isStreaming}
        />
      );
    }
    if (message.direction === MessageDirection.Outgoing) {
      return (
        <Flex key={message.id} direction="column" gap="xs">
          {message.attachments?.length ? <ChatFileCardRow files={message.attachments} /> : null}
          <UserMessage content={message.answerContent || message.response[0]?.content || ''} />
        </Flex>
      );
    }

    if (message.currentState === ChatState.TOOL_LIMIT_REACHED) {
      const hasPrePauseContent = Boolean(
        message.answerContent.trim() || message.thinkingContent.trim(),
      );
      return (
        <Flex key={message.id} direction="column" gap="md">
          {hasPrePauseContent && (
            <SiestaMessage
              content={message.answerContent}
              thinkingContent={message.thinkingContent}
              canCopy
              dark={dark}
            />
          )}
          {message.presentedFiles?.map(file => (
            <PresentedFileCard key={file.url} file={file} />
          ))}
          {renderEmailDrafts(message, messageIndex)}
          {renderProposals(message)}
          <ToolLimitReachedWidget
            onContinue={() => onContinue(message.id)}
            disabled={isStreaming}
          />
        </Flex>
      );
    }

    const isLatest = message.id === latestIncomingId;
    const isToolInFlight = Boolean(message.currentToolName);
    const isThinking =
      isLatest &&
      isStreaming &&
      (isToolInFlight ||
        (message.currentState !== 'ANSWERING' &&
          message.currentState !== 'SENT' &&
          message.currentState !== ''));
    const isActivelyStreaming = isLatest && isStreaming;

    return (
      <Flex key={message.id} direction="column" gap="md">
        <SiestaMessage
          content={message.answerContent}
          thinkingContent={message.thinkingContent}
          isThinking={isThinking}
          currentState={message.currentState}
          currentToolName={isThinking ? message.currentToolName : undefined}
          retryNotice={isLatest && isStreaming ? message.retryNotice : undefined}
          verifyOutcome={message.verifyOutcome}
          evidence={message.evidence}
          verifyMeetingNoteId={message.verifyMeetingNoteId}
          verifyNote={message.verifyNote}
          verifyMessage={message.verifyMessage}
          onVerifyAction={onVerifyAction}
          canCopy={!isActivelyStreaming}
          dark={dark}
        />
        {message.presentedFiles?.map(file => (
          <PresentedFileCard key={file.url} file={file} />
        ))}
        {message.scenarioUpdates?.map((update, index) => (
          <ScenarioUpdateCard key={`${update.scenarioId}-${index}`} update={update} />
        ))}
        {renderEmailDrafts(message, messageIndex)}
        {renderProposals(message)}
      </Flex>
    );
  };

  return (
    <Flex direction="column" gap="md" px="md" py="md">
      {messages.slice(0, anchorIndex).map((message, index) => renderMessage(message, index))}
      {renderOrphanDrafts()}
      {messages
        .slice(anchorIndex)
        .map((message, index) => renderMessage(message, anchorIndex + index))}
      {orphanProposals?.map(proposal => renderProposal(null, proposal))}
      {isReconnecting && (
        <Group justify="center" gap="xs" c="dimmed" my="xs">
          <Loader size="xs" color="gray" />
          <Text size="sm" c="dimmed">
            Reconnecting...
          </Text>
        </Group>
      )}
    </Flex>
  );
}
